import type {
	StoryEditAction,
	StoryEditBatchRequest,
	StoryEditBatchResponse,
} from 'common/story-edit-operations/types';
import { getResponseErrors } from 'common/resources/api-response';
import { StoryEditSaveQueue } from './save-queue';

/** Sent once no action has been captured for this long. */
const IDLE_DELAY_MS = 300;
/** Sent at the latest this long after the oldest unsent action. */
const MAX_WAIT_MS = 2000;
const FIRST_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;

/** The server refuses the request itself; resending it cannot succeed. */
export type StoryEditRejectedStatus = 400 | 413;
/** The session cannot save this story; 401 keeps the batch queued, 403 and 404 drop it. */
export type StoryEditNoAccessStatus = 401 | 403 | 404;

const isRejectedStatus = (status: number | undefined): status is StoryEditRejectedStatus =>
	status === 400 || status === 413;

export type StoryEditSaveStatus = 'DRAFT' | 'SYNC' | 'SUCCESS' | 'FAIL';

export type StoryEditSaver = {
	enqueue(action: StoryEditAction): void;
	/** Sends now; resolves true once the queue is empty, false if a send failed or was rejected. */
	flush(): Promise<boolean>;
	whenNoRequestInFlight(): Promise<void>;
	/** Starts no request until every hold is released; actions still queue. Returns the release. */
	hold(): () => void;
	readonly pending: readonly StoryEditAction[];
};

type StoryEditSaverDeps = {
	storyId: string;
	post(request: StoryEditBatchRequest): Promise<StoryEditBatchResponse>;
	baseOpCounter(): number;
	onStatus(status: StoryEditSaveStatus): void;
	/** The response is not behind and has no skips: local state matches the server. */
	onSaved(response: StoryEditBatchResponse): void;
	/** The response applied at least one action, behind or with skips or not: the story changed on the server. */
	onAcknowledged(): void;
	onCatchUpNeeded(): void;
	onSkipped(): void;
	/** Tells the user that the request was rejected and its actions dropped. */
	onRejected(status: StoryEditRejectedStatus): void;
	/** The no-access handling of the snapshot save path for the same status. */
	onNoAccess(status: StoryEditNoAccessStatus, error: unknown): void;
};

type RequestOutcome = 'saved' | 'failed' | 'rejected';

const savers = new Map<string, StoryEditSaver>();

/** The live saver of a story in this tab, created by the middleware. */
export function storyEditSaverFor(storyId: string): StoryEditSaver | undefined {
	return savers.get(storyId);
}

/** Same reading as `onUpdateVersionError`: the backend error's status code, else the response status. */
function httpStatusOf(error: unknown): number | undefined {
	const responseError = error as IBackendResponseError;
	return getResponseErrors(responseError)?.[0]?.statusCode || responseError?.status;
}

/**
 * Delivers the captured actions of one story in batches, one request at a time (see Delivery in
 * docs/specs/story-edit-operations.md). Retryable failures resend the same actions with backoff,
 * indefinitely; 400, 413, 403 and 404 drop the request; 401 keeps it queued until the next action or
 * flush. Becomes the story's live saver.
 */
export function createStoryEditSaver(deps: StoryEditSaverDeps): StoryEditSaver {
	const queue = new StoryEditSaveQueue();
	let idleTimer: ReturnType<typeof setTimeout> | undefined;
	let maxWaitTimer: ReturnType<typeof setTimeout> | undefined;
	let retryTimer: ReturnType<typeof setTimeout> | undefined;
	let retryDelay = FIRST_RETRY_DELAY_MS;
	/** From a failed request until a request is answered. */
	let hasFailed = false;
	/** After a 401: queued actions wait for the next action or flush instead of being retried. */
	let isHeld = false;
	let request: Promise<RequestOutcome> | undefined;
	let holdCount = 0;
	/** Resolves when the last hold is released; set while any hold is active. */
	let holdsReleased: { promise: Promise<void>; resolve: () => void } | undefined;
	let reportedStatus: StoryEditSaveStatus | undefined;

	const reportStatus = () => {
		let status: StoryEditSaveStatus = 'SUCCESS';
		if (hasFailed) status = 'FAIL';
		else if (queue.isInFlight) status = 'SYNC';
		else if (queue.hasUnsent) status = 'DRAFT';
		if (status === reportedStatus) return;
		reportedStatus = status;
		deps.onStatus(status);
	};

	const clearSendTimers = () => {
		clearTimeout(idleTimer);
		clearTimeout(maxWaitTimer);
		idleTimer = undefined;
		maxWaitTimer = undefined;
	};

	const onResponse = (response: StoryEditBatchResponse): RequestOutcome => {
		console.info('[edit-operations] received', { storyId: deps.storyId, response });
		queue.acknowledge(response.actions);
		hasFailed = false;
		retryDelay = FIRST_RETRY_DELAY_MS;
		const hasSkips = response.actions.some(result => result.skipped.length > 0);
		if (hasSkips) deps.onSkipped();
		if (response.behind || hasSkips) deps.onCatchUpNeeded();
		else deps.onSaved(response);
		if (response.actions.some(result => result.status === 'applied')) deps.onAcknowledged();
		return 'saved';
	};

	const dropBatch = (status: number) => {
		const dropped = queue.drop();
		hasFailed = false;
		retryDelay = FIRST_RETRY_DELAY_MS;
		console.warn('[edit-operations] request rejected, actions dropped', {
			storyId: deps.storyId,
			status,
			actionIds: dropped.map(action => action.actionId),
		});
	};

	const onFailure = (error: unknown): RequestOutcome => {
		const status = httpStatusOf(error);
		if (isRejectedStatus(status)) {
			dropBatch(status);
			deps.onRejected(status);
			deps.onCatchUpNeeded();
			return 'rejected';
		}
		if (status === 403 || status === 404) {
			dropBatch(status);
			deps.onNoAccess(status, error);
			deps.onCatchUpNeeded();
			return 'rejected';
		}

		queue.release();
		hasFailed = true;
		clearSendTimers();
		if (status === 401) {
			console.warn('[edit-operations] request unauthorized, actions kept queued', { storyId: deps.storyId });
			isHeld = true;
			deps.onNoAccess(status, error);
			return 'failed';
		}

		console.warn('[edit-operations] request failed, retrying', { storyId: deps.storyId, status, retryDelay });
		retryTimer = setTimeout(() => {
			retryTimer = undefined;
			send();
		}, retryDelay);
		retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY_MS);
		return 'failed';
	};

	/** Actions captured while a request was in flight go next, unless their timers are still waiting. */
	const afterRequest = () => {
		request = undefined;
		reportStatus();
		const isWaiting = isHeld || retryTimer !== undefined || idleTimer !== undefined || maxWaitTimer !== undefined;
		if (queue.hasUnsent && !isWaiting) send();
	};

	function send() {
		clearSendTimers();
		// The release schedules the send.
		if (holdCount > 0) return;
		const actions = queue.startBatch();
		if (!actions) return;
		reportStatus();
		const batch: StoryEditBatchRequest = { schemaVersion: 2, baseOpCounter: deps.baseOpCounter(), actions };
		console.info('[edit-operations] sending', { storyId: deps.storyId, request: batch });
		request = deps.post(batch).then(onResponse, onFailure).finally(afterRequest);
	}

	const scheduleSend = () => {
		// During backoff the retry sends everything queued.
		if (retryTimer !== undefined) return;
		clearTimeout(idleTimer);
		idleTimer = setTimeout(send, IDLE_DELAY_MS);
		maxWaitTimer ??= setTimeout(send, MAX_WAIT_MS);
	};

	const saver: StoryEditSaver = {
		enqueue(action) {
			isHeld = false;
			queue.enqueue(action);
			reportStatus();
			scheduleSend();
		},

		async flush() {
			while (!queue.isIdle) {
				if (holdsReleased) {
					// eslint-disable-next-line no-await-in-loop -- nothing is sent until the holds are released
					await holdsReleased.promise;
					continue;
				}
				if (!request) {
					isHeld = false;
					clearTimeout(retryTimer);
					retryTimer = undefined;
					send();
				}
				// eslint-disable-next-line no-await-in-loop -- every request waits for the previous one
				const outcome = await request;
				if (outcome !== 'saved') return false;
			}
			return true;
		},

		async whenNoRequestInFlight() {
			while (request) {
				// eslint-disable-next-line no-await-in-loop -- a response can start the next request
				await request;
			}
		},

		hold() {
			holdCount += 1;
			if (!holdsReleased) {
				let resolve = () => {};
				const promise = new Promise<void>(done => {
					resolve = done;
				});
				holdsReleased = { promise, resolve };
			}
			let isReleased = false;
			return () => {
				if (isReleased) return;
				isReleased = true;
				holdCount -= 1;
				if (holdCount > 0) return;
				holdsReleased?.resolve();
				holdsReleased = undefined;
				if (queue.hasUnsent && !request && !isHeld) scheduleSend();
			};
		},

		get pending() {
			return queue.pending;
		},
	};

	savers.set(deps.storyId, saver);
	return saver;
}
